/**
 * The FSE Ledger Upload: load LCFS months from the dispensing partner's
 * allocation workbook.
 *
 * Sits on the Allocation ledger, which is where a missing month is noticed. Drop
 * the workbook, tick which of its months to load, see what each would load,
 * answer anything that did not resolve, and load them.
 *
 * ## Nobody types the month — they tick the sheets the file has
 *
 * There was a year+month pair here and it was a trap: a month mis-picked loads
 * real volumes under the wrong label, and no downstream check catches it — the
 * figures reconcile perfectly, against the wrong period. So the file answers:
 * `/lcfs/allocation/preview` lists every month sheet with the period read off
 * that sheet's own dates, and the card offers exactly those, in words
 * ("August 2026"). Loading echoes those periods back.
 *
 * The workbook is year-to-date, so it can carry several months nobody has
 * loaded — Trillium's September file also holds July and August. The months
 * after the ledger's latest are ticked by default, because that is the gap a
 * new file exists to fill; an already-loaded month can be ticked to replace it,
 * and says so.
 *
 * ## Drop, then confirm — never drop-and-commit
 *
 * The dropzone only *stages* the file and asks for a preview; nothing is written
 * until the Load button. Same shape as the calibration-certificate card, and for
 * a sharper reason here: the workbook names sites the partner's way and a filing
 * is keyed on the FSE id CARB validates, so a load without a human looking at
 * that mapping is how a month gets attributed to the wrong pathway. It has
 * happened — 2026-05's two Visalia stations went in swapped.
 *
 * So **Load stays disabled while anything is unresolved**, and the backend
 * refuses such a request too. The button is a convenience; the route is the rule.
 *
 * ## Two things happen, and only one of them is instant
 *
 * Loading commits the station capacities and starts a job that derives the
 * month's per-dairy production from the meters — minutes, not seconds. The
 * ledger is not complete until that finishes, so the card keeps the job's handle
 * in the URL and reports it, rather than declaring success at the 202.
 */

import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconCheck,
  IconFileSpreadsheet,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  useFseRegistry,
  useJob,
  useLoadStationAllocation,
  usePreviewStationAllocation,
  useStationAllocationPreviews,
} from "../../api/hooks";
import type {
  AllocationPreview,
  NewStationInput,
  PreviewSite,
  WorkbookMonth,
} from "../../api/types";
import { useUploadTypeMeta } from "../../auth/useBranding";
import { periodLabelFrom } from "../../lib/datetime";
import { formatApiError } from "../../lib/errors";
import { invalidateLcfsData, refreshInBackground } from "../../lib/invalidate";
import { fmtMmbtu } from "../../lib/mmbtuFormat";
import { useParamPatcher } from "../../routes/batch-detail/useParamPatcher";
import { RunStageLine } from "../RunStageLine";

/** The URL key the running job's handle lives under. */
export const LOAD_JOB_PARAM = "lcfsLoadJob";

/** Sentinel for "create a station" in the per-site picker. */
const CREATE = "__create__";

type Draft = { fseId: string; station: NewStationInput };

/** A site no ticked month could resolve, with the months it appears on. */
type Unanswered = { site: PreviewSite; months: { period: string; mmbtu: string }[] };

/**
 * The months the workbook carries, as the card lists them.
 *
 * A backend that predates `months` sends only the one previewed month, which
 * becomes a one-item list — the card then behaves exactly as it used to.
 */
function workbookMonths(preview: AllocationPreview): WorkbookMonth[] {
  return (
    preview.months ?? [
      {
        sheet: preview.sheet,
        period: preview.period,
        total_mmbtu: preview.total_mmbtu,
        sites: preview.sites.length + preview.unresolved.length,
        loaded: preview.changes.length > 0,
        error: null,
      },
    ]
  );
}

/**
 * Which months start ticked: those after the latest month already loaded.
 *
 * That is the gap a new file is sent to fill. With nothing loaded yet, or
 * nothing newer than what is, it falls back to the month the preview read.
 */
function defaultSelection(preview: AllocationPreview): string[] {
  const months = workbookMonths(preview);
  const loaded = months.filter((m) => m.loaded && m.period).map((m) => m.period as string);
  const latest = loaded.sort().at(-1);
  if (latest) {
    const gap = months
      .filter((m) => m.period && !m.error && !m.loaded && (m.period as string) > latest)
      .map((m) => m.period as string);
    if (gap.length > 0) return gap;
  }
  return [preview.period];
}

/** "June 2026", "July and August 2026", "July, August and September 2026". */
export function monthsLabel(periods: string[]): string {
  const sorted = [...periods].sort();
  const labels = sorted.map(periodLabelFrom);
  const years = new Set(sorted.map((p) => p.slice(0, 4)));
  if (labels.length <= 1) return labels[0] ?? "";
  if (years.size === 1) {
    const year = sorted[0].slice(0, 4);
    const names = labels.map((l) => l.replace(` ${year}`, ""));
    return `${names.slice(0, -1).join(", ")} and ${names.at(-1)} ${year}`;
  }
  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

export function PeriodLoadCard() {
  const meta = useUploadTypeMeta().station_allocation;
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const patchParams = useParamPatcher();

  const [file, setFile] = useState<File | null>(null);
  // Changes on every drop, so a later file never reads an earlier one's
  // per-month previews out of the query cache.
  const [fileKey, setFileKey] = useState(0);
  const [preview, setPreview] = useState<AllocationPreview | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  // Survives the reset that clears the preview on a successful load, because the
  // production job outlives it by minutes and its line has to keep naming the
  // months. A reload picks the job back up from the URL without this, so it is
  // allowed to be empty and every use falls back.
  const [loading, setLoading] = useState<string | null>(null);

  const previewMutation = usePreviewStationAllocation();
  const loadMutation = useLoadStationAllocation();
  const registry = useFseRegistry();
  const jobId = params.get(LOAD_JOB_PARAM);
  const job = useJob(jobId);

  // The drop already answered one month; every other ticked month is fetched
  // by name, in sheet order.
  const ordered = useMemo(() => [...selected].sort(), [selected]);
  const others = useMemo(
    () => ordered.filter((period) => period !== preview?.period),
    [ordered, preview],
  );
  const otherQueries = useStationAllocationPreviews(file, fileKey, others);

  const previews = useMemo(() => {
    const byPeriod = new Map<string, AllocationPreview>();
    if (preview) byPeriod.set(preview.period, preview);
    others.forEach((period, index) => {
      const data = otherQueries[index]?.data;
      if (data) byPeriod.set(period, data);
    });
    return ordered.flatMap((period) => {
      const found = byPeriod.get(period);
      return found ? [found] : [];
    });
  }, [preview, others, otherQueries, ordered]);
  const fetching = otherQueries.some((q) => q.isPending || q.isFetching);
  const failed = otherQueries.find((q) => q.isError);

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setSelected([]);
    setDrafts({});
    previewMutation.reset();
    loadMutation.reset();
  }, [previewMutation, loadMutation]);

  const onDrop = useCallback(
    (files: File[]) => {
      const dropped = files[0];
      if (!dropped) return;
      setFile(dropped);
      setFileKey((key) => key + 1);
      setDrafts({});
      setSelected([]);
      previewMutation.mutate(
        { file: dropped },
        {
          onSuccess: (answer) => {
            setPreview(answer);
            setSelected(defaultSelection(answer));
          },
          onError: () => setPreview(null),
        },
      );
    },
    [previewMutation],
  );

  const fseOptions = useMemo(
    () => [
      ...(registry.data?.items ?? []).map((r) => ({
        value: r.fse_id,
        label: `${r.station_name} · ${r.fse_id}`,
      })),
      { value: CREATE, label: "＋ Create a station for this site" },
    ],
    [registry.data],
  );

  // One answer per site name across every ticked month: a mapping is recorded
  // by name, so a site unresolved on July and August is one question, not two.
  const unanswered = useMemo(() => {
    const bySite = new Map<string, Unanswered>();
    for (const p of previews) {
      for (const site of p.unresolved) {
        const entry = bySite.get(site.source_name) ?? { site, months: [] };
        entry.months.push({ period: p.period, mmbtu: site.mmbtu });
        bySite.set(site.source_name, entry);
      }
    }
    return [...bySite.values()];
  }, [previews]);

  const answered = useMemo(
    () =>
      unanswered.every(({ site }) => {
        const draft = drafts[site.source_name];
        if (!draft) return false;
        if (draft.fseId !== CREATE) return Boolean(draft.fseId);
        const { fse_id, station_name, dispenser } = draft.station;
        return Boolean(fse_id && station_name && dispenser);
      }),
    [unanswered, drafts],
  );
  const complete = ordered.length > 0 && previews.length === ordered.length;

  const onLoad = useCallback(() => {
    if (!file || !complete) return;
    // The months that commit are the ones the previews reported and the
    // operator just read on the button — never re-derived here.
    const periods = previews.map((p) => p.period);
    const label = monthsLabel(periods);
    const mappings: Record<string, string> = {};
    const newStations: NewStationInput[] = [];
    for (const { site } of unanswered) {
      const draft = drafts[site.source_name];
      if (!draft) continue;
      if (draft.fseId === CREATE) {
        mappings[site.source_name] = draft.station.fse_id;
        newStations.push(draft.station);
      } else {
        mappings[site.source_name] = draft.fseId;
      }
    }
    loadMutation.mutate(
      {
        file,
        periods,
        reason: `Load ${label} station allocations from ${file.name}`,
        mappings,
        newStations,
      },
      {
        onSuccess: (started) => {
          patchParams({ [LOAD_JOB_PARAM]: started.job_id });
          setLoading(label);
          notifications.show({
            color: "teal",
            title: `${label} FSE ledger loaded`,
            message: "Deriving per-dairy production from the meters…",
          });
          reset();
        },
      },
    );
  }, [file, complete, previews, unanswered, drafts, loadMutation, patchParams, reset]);

  // The job settling is what completes the load, so the caches drop then — not
  // at the 202, when production is still minutes away and the ledger would
  // refetch the half of the period that exists.
  const state = job.data?.state;
  const settledJobId = job.data?.job_id ?? null;
  // Clearing `loading` re-runs this effect before the URL has dropped the job,
  // which used to announce the same completion twice. Settle each job once.
  const settled = useRef<string | null>(null);
  useEffect(() => {
    if (state !== "done" && state !== "error") return;
    if (settled.current === settledJobId) return;
    settled.current = settledJobId;
    if (state === "done") {
      notifications.show({
        color: "teal",
        title: loading ? `${loading} is complete` : "The load is complete",
        message: "Production derived; the ledger is up to date.",
      });
      refreshInBackground(invalidateLcfsData(queryClient));
    }
    setLoading(null);
    patchParams({ [LOAD_JOB_PARAM]: null });
  }, [state, settledJobId, loading, queryClient, patchParams]);

  const running = job.data && !["done", "error"].includes(job.data.state);
  // "2 of 3 months" only when there is more than one; a single month's line
  // reads as it always did.
  const total = job.data?.total ?? 1;
  const progress = total > 1 ? ` (${job.data?.progress ?? 0} of ${total} months)` : "";

  return (
    <Card withBorder radius="md" p="md" data-test="lcfs-period-load" data-tour="lcfs-load-card">
      <Stack gap="md">
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <ThemeIcon variant="light" size={34} radius="md">
            <IconFileSpreadsheet size={18} />
          </ThemeIcon>
          <Stack gap={2}>
            <Text fw={600}>{meta.label}</Text>
            <Text size="sm" c="dimmed" maw={720}>
              {meta.description}
            </Text>
          </Stack>
        </Group>

        {running ? (
          <RunStageLine
            label={
              loading
                ? `Deriving ${loading} production from the meters…${progress}`
                : `Deriving production from the meters…${progress}`
            }
            stage={job.data?.stage}
            data-test="lcfs-load-running"
          />
        ) : null}

        {job.data?.state === "error" ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              The capacities loaded, but production could not be derived:{" "}
              {job.data.last_error ?? "no reason given"}. The meters can be re-read without
              re-uploading.
            </Text>
          </Alert>
        ) : null}

        {!file ? (
          <Dropzone
            onDrop={onDrop}
            accept={meta.accept}
            maxFiles={1}
            maxSize={meta.maxSizeMb * 1024 * 1024}
            loading={previewMutation.isPending}
            disabled={Boolean(running)}
          >
            <Group justify="center" gap="sm" mih={80} style={{ pointerEvents: "none" }}>
              <Dropzone.Accept>
                <IconUpload size={22} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={22} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconUpload size={22} />
              </Dropzone.Idle>
              <Text size="sm" c="dimmed">
                Drop the workbook — then tick which of its months to load
              </Text>
            </Group>
          </Dropzone>
        ) : (
          <Group gap="xs">
            <Badge variant="light" radius="sm">
              {file.name}
            </Badge>
            <Button variant="subtle" size="compact-sm" onClick={reset}>
              Choose a different file
            </Button>
          </Group>
        )}

        {previewMutation.isError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">{formatApiError(previewMutation.error)}</Text>
          </Alert>
        ) : null}

        {preview ? (
          <MonthPicker
            months={workbookMonths(preview)}
            selected={selected}
            onChange={setSelected}
          />
        ) : null}

        {failed ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">{formatApiError(failed.error)}</Text>
          </Alert>
        ) : null}

        {fetching ? (
          <Group gap={6}>
            <Loader size={12} />
            <Text size="sm" c="dimmed">
              Reading the ticked months…
            </Text>
          </Group>
        ) : null}

        {unanswered.length > 0 ? (
          <UnansweredSites
            unanswered={unanswered}
            drafts={drafts}
            setDrafts={setDrafts}
            fseOptions={fseOptions}
            multi={ordered.length > 1}
          />
        ) : null}

        {previews.length === 1 ? <PreviewBody preview={previews[0]} /> : null}
        {previews.length > 1 ? (
          <Accordion variant="separated" multiple>
            {previews.map((p) => (
              <Accordion.Item key={p.period} value={p.period}>
                <Accordion.Control>
                  <Group gap="sm">
                    <Text size="sm" fw={600}>
                      {periodLabelFrom(p.period)}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {fmtMmbtu(p.total_mmbtu)} MMBtu · {p.sites.length + p.unresolved.length} sites
                    </Text>
                    {p.changes.length > 0 ? (
                      <Badge size="xs" variant="light" color="orange">
                        replaces loaded figures
                      </Badge>
                    ) : null}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <PreviewBody preview={p} />
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        ) : null}

        {loadMutation.isError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">{formatApiError(loadMutation.error)}</Text>
          </Alert>
        ) : null}

        {preview ? (
          <Group justify="flex-end">
            <Tooltip
              label={
                ordered.length === 0
                  ? "Tick at least one month to load."
                  : "Every site has to resolve to a station before the months can load."
              }
              disabled={answered && complete}
              withArrow
            >
              <Button
                onClick={onLoad}
                loading={loadMutation.isPending}
                disabled={!answered || !complete || Boolean(running)}
                data-test="lcfs-load-period"
                data-tour="lcfs-load-period"
              >
                {ordered.length === 0 ? "Load" : `Load ${monthsLabel(ordered)}`}
              </Button>
            </Tooltip>
          </Group>
        ) : null}
      </Stack>
    </Card>
  );
}

/**
 * One checkbox per month sheet. A sheet that would not read is listed with its
 * reason and cannot be ticked; a month already on the ledger says that loading
 * it replaces what is there.
 */
function MonthPicker({
  months,
  selected,
  onChange,
}: {
  months: WorkbookMonth[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <Stack gap="xs" data-test="lcfs-load-months">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
        Months in this workbook
      </Text>
      <Checkbox.Group value={selected} onChange={onChange}>
        <Stack gap={6}>
          {months.map((month) => (
            <Checkbox
              key={month.sheet}
              value={month.period ?? `unreadable:${month.sheet}`}
              disabled={!month.period || Boolean(month.error)}
              label={
                <Group gap={6} wrap="nowrap">
                  <Text size="sm">
                    {month.period ? periodLabelFrom(month.period) : month.sheet}
                  </Text>
                  {month.loaded ? (
                    <Tooltip
                      label="This month is already on the ledger. Loading it replaces those figures."
                      multiline
                      w={260}
                      withArrow
                    >
                      <Badge size="xs" variant="light" color="gray">
                        loaded
                      </Badge>
                    </Tooltip>
                  ) : null}
                </Group>
              }
              description={
                month.error ??
                (month.total_mmbtu
                  ? `${fmtMmbtu(month.total_mmbtu)} MMBtu · ${month.sites} sites · ${month.sheet} sheet`
                  : undefined)
              }
            />
          ))}
        </Stack>
      </Checkbox.Group>
    </Stack>
  );
}

function UnansweredSites({
  unanswered,
  drafts,
  setDrafts,
  fseOptions,
  multi,
}: {
  unanswered: Unanswered[];
  drafts: Record<string, Draft>;
  setDrafts: (next: (prev: Record<string, Draft>) => Record<string, Draft>) => void;
  fseOptions: { value: string; label: string }[];
  multi: boolean;
}) {
  return (
    <Stack gap="xs">
      <Group gap="sm">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
          Sites with no station
        </Text>
        <Text size="xs" fw={700} c="orange">
          {unanswered.length} need a station
        </Text>
      </Group>
      <Text size="xs" c="dimmed">
        These names are not in the roster and have never been mapped. Pick the station each one is,
        or create it — the answer is remembered, so next month resolves itself.
        {multi ? " One answer covers every ticked month the name appears on." : ""}
      </Text>
      <Table verticalSpacing="xs">
        <Table.Tbody>
          {unanswered.map(({ site, months }) => (
            <UnresolvedRow
              key={site.source_name}
              site={site}
              months={multi ? months : null}
              draft={drafts[site.source_name]}
              fseOptions={fseOptions}
              onChange={(next) => setDrafts((prev) => ({ ...prev, [site.source_name]: next }))}
            />
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function PreviewBody({ preview }: { preview: AllocationPreview }) {
  return (
    <Stack gap="md">
      <Group gap="lg" wrap="wrap">
        {/* The month leads the preview: it is the one thing on this card read
            off the file rather than chosen, so it is the one thing that has to
            be read before loading. The sheet it came off hangs off it as
            provenance. */}
        <Stat
          label="Month"
          value={periodLabelFrom(preview.period)}
          hint={`Read from the workbook's ${preview.sheet} sheet`}
        />
        <Stat label="Total" value={`${fmtMmbtu(preview.total_mmbtu)} MMBtu`} />
        <Stat label="Sites" value={String(preview.sites.length + preview.unresolved.length)} />
        {preview.unresolved.length > 0 ? (
          <Stat label="Need a station" value={String(preview.unresolved.length)} warn />
        ) : (
          <Group gap={6}>
            <IconCheck size={16} color="var(--mantine-color-teal-6)" />
            <Text size="sm" c="dimmed">
              Every site resolved
            </Text>
          </Group>
        )}
      </Group>

      {preview.sites.length > 0 ? (
        <Stack gap="xs">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
            Resolved
          </Text>
          <Table verticalSpacing="xs">
            <Table.Tbody>
              {preview.sites.map((site) => (
                <Table.Tr key={site.source_name}>
                  <Table.Td>
                    <Text size="sm">{site.source_name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Text size="sm" c="dimmed">
                        → {site.station_name ?? site.fse_id}
                      </Text>
                      {/* A recorded decision and a name that happened to match
                          are not the same evidence, so the weaker one says so. */}
                      {site.via === "station_name" ? (
                        <Tooltip
                          label="Matched on the station's name rather than a recorded mapping. Loading records it, so next month it is a mapping."
                          multiline
                          w={280}
                          withArrow
                        >
                          <Badge size="xs" variant="light" color="gray">
                            by name
                          </Badge>
                        </Tooltip>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right" w={120}>
                    <Text size="sm" ff="monospace" style={{ fontVariantNumeric: "tabular-nums" }}>
                      {fmtMmbtu(site.mmbtu)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      ) : null}

      {preview.changes.length > 0 ? (
        <Stack gap="xs">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
            What this changes
          </Text>
          <Text size="xs" c="dimmed">
            {periodLabelFrom(preview.period)} already holds figures. Loading replaces them.
          </Text>
          <Table verticalSpacing="xs">
            <Table.Tbody>
              {preview.changes.map((change) => (
                <Table.Tr key={change.fse_id}>
                  <Table.Td>
                    <Text size="sm">{change.station_name ?? change.fse_id}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" ff="monospace" c="dimmed">
                      {change.before ? fmtMmbtu(change.before) : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td w={20} ta="center">
                    <Text size="sm" c="dimmed">
                      →
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" ff="monospace">
                      {change.after ? fmtMmbtu(change.after) : "removed"}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      ) : null}
    </Stack>
  );
}

function UnresolvedRow({
  site,
  months,
  draft,
  fseOptions,
  onChange,
}: {
  site: PreviewSite;
  /** Each ticked month the name appears on, when more than one is ticked. */
  months: { period: string; mmbtu: string }[] | null;
  draft: Draft | undefined;
  fseOptions: { value: string; label: string }[];
  onChange: (next: Draft) => void;
}) {
  const creating = draft?.fseId === CREATE;
  const station = draft?.station ?? { fse_id: "", station_name: site.source_name, dispenser: "" };

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Text size="sm">{site.source_name}</Text>
        </Table.Td>
        <Table.Td ta="right" w={months ? 220 : 120}>
          {months ? (
            <Stack gap={0}>
              {months.map((m) => (
                <Text
                  key={m.period}
                  size="sm"
                  ff="monospace"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {fmtMmbtu(m.mmbtu)}{" "}
                  <Text span size="xs" c="dimmed" ff="inherit">
                    {periodLabelFrom(m.period)}
                  </Text>
                </Text>
              ))}
            </Stack>
          ) : (
            <Text size="sm" ff="monospace" style={{ fontVariantNumeric: "tabular-nums" }}>
              {fmtMmbtu(site.mmbtu)}
            </Text>
          )}
        </Table.Td>
        <Table.Td w={340}>
          <Select
            placeholder="Pick a station"
            data={fseOptions}
            value={draft?.fseId ?? null}
            onChange={(value) => onChange({ fseId: value ?? "", station })}
            searchable
            comboboxProps={{ withinPortal: true }}
            aria-label={`Station for ${site.source_name}`}
          />
        </Table.Td>
      </Table.Tr>
      {creating ? (
        <Table.Tr>
          <Table.Td colSpan={3}>
            <Group gap="xs" wrap="wrap" pl="md">
              <TextInput
                label="FSE ID"
                description="The identifier CARB validates a fuel transaction row against"
                value={station.fse_id}
                onChange={(e) =>
                  onChange({
                    fseId: CREATE,
                    station: { ...station, fse_id: e.currentTarget.value },
                  })
                }
                w={220}
              />
              <TextInput
                label="Station name"
                value={station.station_name}
                onChange={(e) =>
                  onChange({
                    fseId: CREATE,
                    station: { ...station, station_name: e.currentTarget.value },
                  })
                }
                w={260}
              />
              <TextInput
                label="Dispenser"
                description="The group this site sits under on the sheet"
                value={station.dispenser}
                onChange={(e) =>
                  onChange({
                    fseId: CREATE,
                    station: { ...station, dispenser: e.currentTarget.value },
                  })
                }
                w={200}
              />
            </Group>
          </Table.Td>
        </Table.Tr>
      ) : null}
    </>
  );
}

function Stat({
  label,
  value,
  warn,
  hint,
}: {
  label: string;
  value: string;
  warn?: boolean;
  hint?: string;
}) {
  const body = (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.04em">
        {label}
      </Text>
      <Text size="sm" fw={700} c={warn ? "orange" : undefined}>
        {value}
      </Text>
    </Stack>
  );
  return hint ? (
    <Tooltip label={hint} withArrow>
      {body}
    </Tooltip>
  ) : (
    body
  );
}
